import { ChevronRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { ReferralOwnerRow, ReferralOwnerType } from '@manaro/contracts'
import { cn } from '@/shared/lib/cn.ts'
import { formatDate } from '@/shared/lib/format.ts'
import { Badge } from '@/shared/ui/badge.tsx'
import { CardHeading, CardStat, CardStats, mobileCardClass } from '@/shared/ui/responsive-table.tsx'
import { referralOwnerPath } from '../filters.ts'

interface ReferralOwnerCardProps {
  row: ReferralOwnerRow
  ownerType: ReferralOwnerType
  listSearch: string
}

function ownerCounts(row: ReferralOwnerRow, ownerType: ReferralOwnerType): Array<{ label: string; value: number }> {
  const businessOnly =
    ownerType === 'business'
      ? [
          { label: 'Пользователей', value: row.users },
          { label: 'Бизнесов', value: row.businesses },
        ]
      : []

  return [{ label: 'Приглашено', value: row.invited }, ...businessOnly, { label: 'Активных', value: row.active }]
}

/** One referral owner on a phone; the whole card opens the owner page, like a row does on desktop. */
export function ReferralOwnerCard({ row, ownerType, listSearch }: ReferralOwnerCardProps) {
  return (
    <li>
      <Link
        to={referralOwnerPath(row.code)}
        state={{ back: listSearch }}
        className={cn(mobileCardClass, 'flex-row items-center transition-colors active:bg-surface-hover')}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-2.5">
          {/* The email sits under the heading row, so the suspicious badge never squeezes it. */}
          <div>
            <CardHeading
              aside={
                row.suspicious > 0 && (
                  <Badge tone="warn">
                    подозрительных: <span className="tabular">{row.suspicious}</span>
                  </Badge>
                )
              }
            >
              <div className="truncate text-[15px] font-semibold">{row.ownerName || row.ownerEmail || '—'}</div>
            </CardHeading>
            {row.ownerName && row.ownerEmail && (
              <div className="mt-0.5 truncate text-[12px] text-ink-muted">{row.ownerEmail}</div>
            )}
          </div>

          <CardStats>
            {ownerCounts(row, ownerType).map(({ label, value }) => (
              <CardStat key={label} label={label}>
                <span className={cn('tabular', value === 0 && 'font-normal text-ink-faint')}>{value}</span>
              </CardStat>
            ))}
          </CardStats>

          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-[12px] text-ink-muted">
            <span className="font-mono text-[13px] text-ink">{row.code}</span>
            {row.createdAt && <span>код создан {formatDate(row.createdAt)}</span>}
          </div>
        </div>
        <ChevronRight className="size-4 shrink-0 text-ink-faint" aria-hidden />
      </Link>
    </li>
  )
}
